/**
 * The two decisions a select-shaped field makes that are not about *how* it is
 * rendered: whether the clear row is a row at all, and what closing the field
 * does to the value.
 *
 * Pure functions, and deliberately not part of `useSelectSelection`: each of
 * them used to be written out at every site that needed it — the clear-row rule
 * three times per component, the close rule inside a watcher — and every
 * regression this pair produced in review was the same accident, a condition
 * corrected where the symptom appeared and left standing next to it. One rule
 * with a truth table around it cannot drift; a repeated expression always can.
 */

/** The state that decides whether the clear row can be aimed at. */
export type PlaceholderRowState = {
  allowNull?: boolean;
  multiple?: boolean;
  /** Whether a query is narrowing the list right now. */
  filtering?: boolean;
};

/**
 * Whether the clear row (`-1`) can be aimed at — by the highlight, by Enter and
 * by the arrow keys.
 *
 * A multi-select renders none — clearing every value at once is not what
 * "remove this one" means, and `SelectOptionList` leaves the row out. While a
 * query is active it is hidden too: the clear row is an action, not a match, so
 * it must not sit above the options the user is filtering for. Where null is not
 * allowed the row is still drawn, greyed out — reachable is the narrower
 * question, and it is the one the keyboard has to ask.
 *
 * Read by the highlight's resting position, by the arrow keys' lower bound and
 * by Enter — one answer, so those three cannot disagree about what `-1` means.
 */
export function placeholderRowReachable(state: PlaceholderRowState): boolean {
  return Boolean(state.allowNull) && !state.multiple && !state.filtering;
}

/** What closing the field does to the value. */
export type SelectCloseAction =
  /** Escape: drop whatever a lazy model has not committed. */
  | 'revert'
  /** The user emptied the field: clear the value, then commit. */
  | 'clear'
  /** Ordinary close: commit a pending lazy value, leave the value alone. */
  | 'commit';

/** Everything closing a select depends on. */
export type SelectCloseState = {
  /** The session was abandoned with Escape. */
  cancelled: boolean;
  /** The text in the field is the user's, not the value it was opened with. */
  queryEdited: boolean;
  query: string;
  /** An option (or the clear row) was chosen since the field opened. */
  picked: boolean;
  allowNull?: boolean;
  multiple?: boolean;
};

/**
 * Decide what a closing field does with its value.
 *
 * An emptied field is a cleared value: now that the selection sits in the input
 * as real text, deleting it is the obvious way to say "none", and without this it
 * would be the one gesture that silently does nothing. It is a *clear* only where
 * the clear row would have been offered, and only if the user did not go on to
 * pick something — otherwise emptying the text and then choosing an option would
 * commit `null` over the pick on the way out.
 *
 * "Emptied" means empty, not blank: the filter trims a query (a space matches
 * everything, so it is not a filter), but a field holding a space is one the user
 * typed into, not one they cleared. Trimming here instead turned the space bar
 * into a delete key on every enum field.
 */
export function selectCloseAction(state: SelectCloseState): SelectCloseAction {
  if (state.cancelled) return 'revert';

  const emptied = state.queryEdited && state.query === '' && !state.picked;
  if (emptied && placeholderRowReachable({ allowNull: state.allowNull, multiple: state.multiple }))
    return 'clear';

  return 'commit';
}
